import {
  cleanVal,
  dd,
  df,
  dfpct,
  dfzero,
  FeeContext,
  fmtAccount,
  getModelDescription,
  getModelRisk,
  getStdParams,
  setFeesAsParam,
} from "./buildDocumentProductionList";
import {
  QueryModelPosition,
  QueryProfile,
  QuerySecurity,
} from "@/api/queries/types";
import { Contact, Portfolio } from "@/api/types";
import { BuyOrderRow } from "@/forms/forms/orders/orderForm/components/BuyOrders";
import { SellOrderRow } from "@/forms/forms/orders/orderForm/components/SellOrders";
import {
  getPendingInternalTransferTargets,
  type InternalTransferOrder,
} from "@/forms/forms/orders/orderForm/internalTransfer";
import { SERVICE_TYPE_LABELS } from "@/forms/forms/orders/orderForm/OrderForm.options";
import { PendingOrder } from "@/forms/forms/quality/suitability/SuitabilityForm.types";
import { DocProducerDocument } from "@/views/docproducer";

// ---------------------------------------------------------------------------
// Output type
// ---------------------------------------------------------------------------

export type DocumentWithVariables = {
  /** Source DocProducer document id */
  id: string;
  /** docType of the source document, e.g. "order", "meetingMinutes" */
  type: string;
  /** Every variable belonging to this document, collected in one place */
  data: Record<string, unknown>;
};

export type DocumentListWithVariables = {
  standardParams: Record<string, string>;
  kundeprofil: QueryProfile[];
  documents: DocumentWithVariables[];
};

// ---------------------------------------------------------------------------
// Order variable collection
// ---------------------------------------------------------------------------

type SecurityMetadata = {
  securityTypeCode: string | null;
  securityTypeName: string | null;
  managementFee: number | null;
  kickback: number | null;
  esg: string | null;
  sfdr: string | null;
};

type OrderDataWarning = {
  path: string;
  securityCode?: string;
  missingFields: string[];
};

function textOrNull(value: string | null | undefined): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function textNumberOrNull(
  value: string | number | null | undefined
): string | null {
  return value != null && value !== "" ? String(value) : null;
}

function numberOrNull(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function norwegianSecurityTypeName(security?: QuerySecurity): string | null {
  for (const entry of (security?.securityTypeNames ?? "").split(";")) {
    const separator = entry.indexOf("=");
    if (separator >= 0 && entry.slice(0, separator).trim() === "no_NO") {
      const name = textOrNull(entry.slice(separator + 1));
      if (name) return name;
    }
  }
  return textOrNull(security?.securityTypeName);
}

function getSecurityMetadata(
  security?: QuerySecurity,
  position?: QueryModelPosition
): SecurityMetadata {
  return {
    securityTypeCode: textOrNull(security?.securityTypeCode),
    securityTypeName: norwegianSecurityTypeName(security),
    managementFee:
      numberOrNull(security?.managementFeePercentage) ??
      numberOrNull(position?.management_fee_percentage),
    kickback:
      numberOrNull(security?.kickbackPercentage) ??
      numberOrNull(position?.kickback_percentage),
    esg: textNumberOrNull(security?.esg) ?? textNumberOrNull(position?.esg),
    sfdr: textOrNull(security?.sfdr) ?? textOrNull(position?.sfdr),
  };
}

/**
 * Gathers every variable an "order" document could ever produce — account/
 * management/model agreement fields, one-off deposit, transfer authorization,
 * withdrawal, manual buy/sell/rebalance lines, recurring savings and
 * recurring withdrawal — always, regardless of whether the underlying amount
 * or toggle is set. Sales rows require a positive quantity; other empty/zero
 * values are left in as-is. Which variables are actually relevant for
 * production is decided by the backend.
 *
 * `manualOrderLines` and `savingsFunds` are nested rather than flattened
 * because both use numbered keys (`sec1`/`amount1` vs `fund1Name`/`amount1`)
 * that would otherwise collide once merged into a single object.
 *
 * Layout: raw input and derived values are computed first; `variables` then
 * starts empty and is filled section by section, in this order:
 *   1. Portfolio identity     5. Fees & cost type      9. Manual order lines
 *   2. Service type           6. One-off deposit      10. Dynamic order options
 *   3. Model / mandate        7. Transfer authorization 11. Recurring savings
 *   4. Value & investment     8. One-off withdrawal    12. Recurring withdrawal
 * (IPS settings sit under 4.) Fee variables may overwrite earlier keys, so
 * sections 1–5 must stay before the fee block.
 */
function collectOrderVariables(
  doc: DocProducerDocument,
  baseParams: Record<string, string>,
  faModels: QueryModelPosition[],
  faSec: QuerySecurity[],
  orders: InternalTransferOrder[]
): Record<string, unknown> {
  const raw = doc.data as Record<string, unknown>;
  // Legacy exports already contain production variables; reverse mapping loses information.
  if (
    typeof raw.legacyExportData === "object" &&
    raw.legacyExportData !== null &&
    !Array.isArray(raw.legacyExportData)
  ) {
    return { ...baseParams, ...raw.legacyExportData };
  }
  const newData = (raw?.newData ?? raw) as Record<string, unknown>;
  const existData = (raw?.existData ?? {}) as Record<string, unknown>;

  // ── Raw input (read from the order document) ───────────────────────────────
  const portId = cleanVal(newData.dpPortfolioId);
  const portName = cleanVal(
    newData.dpPortfolioName || existData.dpPortfolioName || "Portefølje"
  );
  const portShortName = cleanVal(
    existData.dpPortfolioShortName || newData.dpPortfolioShortName || ""
  );
  const portType = cleanVal(newData.dpPortType || existData.dpPortType || "");
  const modelType = cleanVal(
    newData.dpModelType || existData.dpModelType || "NOMODEL"
  );
  const existingModel = existData.dpModelType || "NOMODEL";

  const isExisting =
    portId !== "NEW" &&
    portId !== "" &&
    existData.isExisting !== false &&
    cleanVal(existData.isExisting) !== "false";

  const newFee = dd(newData.dpManagementFee);
  const existingFee =
    existData.dpManagementFee === undefined
      ? newFee
      : dd(existData.dpManagementFee);

  const dpAmountToInvest = dd(newData.dpAmountToInvest);
  const dpSumAvCash = dd(existData.dpSumAvCash ?? newData.dpSumAvCash);
  const dpSumInsValue = dd(existData.dpSumInsValue ?? newData.dpSumInsValue);
  const dpOnOffAmount = dd(newData.dpOnOffAmount);
  const dpWithdrawal = dd(newData.dpWithdrawal);
  const dpOnOffDate = cleanVal(newData.dpOnOffDate);
  const rawDpAccount = cleanVal(newData.dpAccount);
  const subscriptionFeePercent = dd(newData.dpOnOffPercentage);
  const subscriptionFeePercentFormatted = dfpct(subscriptionFeePercent);
  const dpAccount = fmtAccount(
    cleanVal(newData.dpAccount || newData.dpNewAccount || "")
  );
  const isNewAccount = rawDpAccount === "newAccount";
  const dpNewAccount = isNewAccount
    ? fmtAccount(cleanVal(newData.dpNewAccount))
    : "";

  const dpIpsWithdrawalAge = dd(newData.dpIpsWithdrawalAge);
  const dpIpsAnnualSavings = dd(newData.dpIpsAnnualSavings);

  const moveFrom = cleanVal(newData.moveFrom || existData.moveFrom || "");
  const moveFromId = cleanVal(newData.moveFromId || existData.moveFromId || "");
  const moveAll = cleanVal(newData.moveAll || existData.moveAll || "");

  const buyOrders = (newData.buyOrders as BuyOrderRow[]) || [];
  const sellOrders = ((newData.sellOrders as SellOrderRow[]) || []).filter(
    (row) => dd(row.sellQty) > 0
  );

  // ── Derived values ──────────────────────────────────────────────────────────
  const portAmount = dpSumAvCash + dpSumInsValue + dpAmountToInvest;
  const totalAmount = dpSumAvCash + dpSumInsValue + dpAmountToInvest;
  const isCap = ["20", "21", "22", "23", "24"].includes(portType);
  const isModel = modelType !== "NOMODEL" && modelType !== "";
  const modelPositions = isModel
    ? faModels.filter((position) => position.short_name === modelType)
    : [];
  const findPositionSecurity = (position: QueryModelPosition) => {
    const isin = cleanVal(position.isin_code).trim().toUpperCase();
    const code = cleanVal(position.security_code).trim().toUpperCase();
    return (
      (isin
        ? faSec.find(
            (security) =>
              cleanVal(security.isin_code).trim().toUpperCase() === isin
          )
        : undefined) ??
      (code
        ? faSec.find(
            (security) =>
              cleanVal(security.security_code).trim().toUpperCase() === code
          )
        : undefined)
    );
  };
  const dataWarnings: OrderDataWarning[] = [];
  const collectSecurityMetadata = (
    security: QuerySecurity | undefined,
    path: string,
    securityCode: string,
    position?: QueryModelPosition
  ) => {
    const metadata = getSecurityMetadata(security, position);
    const missingFields = Object.entries(metadata)
      .filter(([, value]) => value === null)
      .map(([key]) => key);
    if (missingFields.length > 0) {
      dataWarnings.push({ path, securityCode, missingFields });
    }
    return metadata;
  };

  const portTypeLabels: Record<string, string> = {
    "20": "Fondskonto",
    "21": "VPS-konto",
    "22": "ASK",
    "23": "IPS",
    "24": "IPS",
  };
  const displayPortName = !isExisting
    ? `${portTypeLabels[portType] ?? "Portefølje"} Ny`
    : portName;
  const portIdLabel =
    isExisting && portShortName
      ? `${portTypeLabels[portType] ?? "Portefølje"} - ${portShortName}`
      : displayPortName;

  const variables: Record<string, unknown> = {};

  // ── 1. Portfolio identity ──────────────────────────────────────────────────
  variables.portId = displayPortName;
  variables.portIdLabel = portIdLabel;
  variables.portName = portName;
  variables.portShortName = portShortName;
  variables.dpPortfolioName = displayPortName;
  variables.dpPortType = portType;
  variables.portAccount = dpAccount;
  variables.dpAccount = isNewAccount ? dpNewAccount : dpAccount;
  variables.isNewAccount = isNewAccount;
  variables.dpNewAccount = dpNewAccount;
  variables.isExisting = isExisting;

  // ── 2. Service type ────────────────────────────────────────────────────────────────
  variables.dpServiceType = cleanVal(newData.dpServiceType);
  variables.serviceType =
    SERVICE_TYPE_LABELS[cleanVal(newData.dpServiceType)] ?? "";

  // ── 3. Model / mandate ─────────────────────────────────────────────────────
  variables.dpModelType = modelType;
  variables.existingModel = existingModel;
  variables.existingAdvancedEKB = cleanVal(
    newData.existingAdvancedEKB ?? existData.existingAdvancedEKB
  );
  variables.advancedEKB =
    modelType === "CAPGUARDEKB" ? cleanVal(newData.advancedEKB) : "";
  variables.AdvancedEKBchange =
    modelType === "CAPGUARDEKB" && newData.AdvancedEKBchange === true;
  variables.actMandat = getModelDescription(modelType);
  variables.actDesc =
    faModels.find((p) => p.short_name === modelType)?.description ??
    getModelDescription(modelType);
  variables.actRisk = getModelRisk(modelType);
  variables.positions = modelPositions.map((position, index) => {
    const security = findPositionSecurity(position);
    const securityCode = cleanVal(
      position.security_code || security?.security_code
    );
    return {
      securityId: security?.id ?? null,
      securityCode,
      isinCode: cleanVal(position.isin_code || security?.isin_code),
      name: cleanVal(position.name || security?.name || position.description),
      shareOfPortfolio: numberOrNull(position.share_of_portfolio),
      ...collectSecurityMetadata(
        security,
        `positions[${index}]`,
        securityCode,
        position
      ),
    };
  });
  if (isModel && modelPositions.length === 0) {
    dataWarnings.push({ path: "positions", missingFields: ["positions"] });
  }

  // ── 4. Value & investment (incl. IPS settings) ─────────────────────────────
  variables.dpSumAvCash = dpSumAvCash;
  variables.dpSumInsValue = dpSumInsValue;
  variables.dpAmountToInvest = dpAmountToInvest;
  variables.dpAmountToInvestFormatted = "NOK " + dfzero(dpAmountToInvest);
  variables.dpTotalAmount = totalAmount;
  variables.dpTotalAmountFormatted = "NOK " + dfzero(totalAmount);
  variables.portAmount = "NOK " + dfzero(portAmount);
  variables.actAmount = `NOK ${dfzero(portAmount)}, nytt volum: NOK ${dfzero(
    dpAmountToInvest
  )}`;
  variables.portIpsage = portType === "23" ? String(dpIpsWithdrawalAge) : "";
  variables.portIpsAmount =
    portType === "23" ? `NOK ${dfzero(dpIpsAnnualSavings)}` : "";

  // ── 5. Fees & cost type ────────────────────────────────────────────────────
  variables.portCostType = isCap ? "Capguardhonoraret" : "Honorar";
  variables.dpSubscriptionFeePercent = subscriptionFeePercent;
  variables.dpSubscriptionFeePercentFormatted = subscriptionFeePercentFormatted;

  const feeCtx: FeeContext = {
    agreedFee: 0,
    accountFee: isCap ? 0.15 : 0.3,
    totCost: 0,
    manFee: 0,
    kickback: 0,
  };
  const feeVariables: Record<string, string> = {};
  setFeesAsParam(
    feeVariables,
    faModels,
    isModel ? modelType : "",
    newFee,
    portAmount,
    feeCtx,
    isCap,
    portType,
    existingFee
  );
  Object.assign(variables, feeVariables);

  // ── 6. One-off deposit ───────────────────────────────────────────────────────
  const descPortfolio =
    displayPortName + " " + (isExisting ? portShortName : "");
  const descOrderType = isModel
    ? getModelDescription(modelType)
    : "Manuell ordre";
  variables.dpOnOffAmount = dpOnOffAmount;
  variables.dpOnOffDate = dpOnOffDate;
  variables.onOffAmt = "NOK " + dfzero(dpOnOffAmount);
  variables.onOffDesc = descPortfolio + "\n" + descOrderType;
  variables.portOnOffDate = dpOnOffDate || "";

  // ── 7. Transfer authorization (move from another provider) ─────────────────
  variables.moveFrom = moveFrom;
  variables.moveFromId = moveFromId;
  variables.moveAskType = "Fra Aksjesparekonto til Aksjesparekonto";
  variables.movePart = moveAll
    ? "Hele min beholdning"
    : "Deler av min beholdning";
  variables.moveAskport = !isExisting ? "Under opprettelse" : portShortName;
  for (let i = 1; i < 10; i++) {
    variables[`Instrument${i}`] = cleanVal(
      newData[`Instrument${i}`] || existData[`Instrument${i}`] || ""
    );
    variables[`InstrumentAmt${i}`] = cleanVal(
      newData[`InstrumentAmt${i}`] || existData[`InstrumentAmt${i}`] || ""
    );
  }

  // ── 8. Withdrawal (one-off) ─────────────────────────────────────────────────
  variables.dpClosePortfolio = newData.dpClosePortfolio === true;
  const withdrawalInternalTransfer =
    dpWithdrawal > 0 && newData.dpWithdrawalInternalTransfer === true;
  const withdrawalTargetPortfolioId = cleanVal(newData.dpWithdrawalTargetPortfolioId);
  const pendingTarget = getPendingInternalTransferTargets(orders, doc.id).find(
    (target) => target.id === withdrawalTargetPortfolioId
  );
  const withdrawalTargetPortfolioShortName = pendingTarget?.shortName ??
    cleanVal(newData.dpWithdrawalTargetPortfolioShortName);
  if (
    withdrawalInternalTransfer &&
    (!isExisting ||
      !portShortName.startsWith("2") ||
      (!pendingTarget && !withdrawalTargetPortfolioShortName.startsWith("2")) ||
      !withdrawalTargetPortfolioId ||
      withdrawalTargetPortfolioId === portId)
  ) {
    throw new Error("Intern overføring krever ulike Capguard-porteføljer. En ny mottakerportefølje må fortsatt finnes som en Capguard-ordre i dokumentlisten.");
  }
  variables.dpWithdrawal = dpWithdrawal;
  variables.dpWithdrawalInternalTransfer = withdrawalInternalTransfer;
  variables.dpWithdrawalTargetPortfolioId = withdrawalInternalTransfer
    ? withdrawalTargetPortfolioId
    : "";
  variables.dpWithdrawalTargetPortfolioShortName = withdrawalInternalTransfer
    ? withdrawalTargetPortfolioShortName
    : "";
  variables.dpWithdrawalTargetPortfolioName = withdrawalInternalTransfer
    ? pendingTarget?.name ?? cleanVal(newData.dpWithdrawalTargetPortfolioName)
    : "";
  variables.wdAmount = "NOK " + dfzero(dpWithdrawal);
  variables.wdAcc = rawDpAccount === "newAccount" ? "newAccount" : dpAccount;

  // ── 9. Manual buy / sell / rebalance order lines (nested — numbered keys) ──
  // Only populated when there actually are manual buy/sell rows — a model
  // order with no manual rows has nothing to list here (mirrors the
  // `hasBuyOrders || hasSellOrders` gate the PDF-mapping logic used).
  const hasBuyOrders = buyOrders.some(
    (o) => o.securityId !== null && o.securityId !== 0
  );
  const hasSellOrders = sellOrders.some((o) => o.securityId !== 0);
  const manualOrderLines: Record<string, string | number | null> = {};
  const addManualSecurityMetadata = (
    count: number,
    security: QuerySecurity | undefined,
    securityCode: string,
    position?: QueryModelPosition
  ) => {
    const metadata = collectSecurityMetadata(
      security,
      `manualOrderLines.sec${count}`,
      securityCode,
      position
    );
    for (const [key, value] of Object.entries(metadata)) {
      manualOrderLines[`${key}${count}`] = value;
    }
  };
  if (hasBuyOrders || hasSellOrders) {
    let count = 1;
    for (const order of buyOrders) {
      if (!order.securityId) continue;
      const secDet = faSec.find((s) => s.id === order.securityId);
      const secCode =
        secDet?.isin_code ?? order.isinCode ?? order.instrumentCode;
      manualOrderLines[`sec${count}`] = `${order.instrumentName}\n${secCode}`;
      manualOrderLines[`secName${count}`] = `${order.instrumentName}`;
      manualOrderLines[`secIsin${count}`] = `${secCode}`;
      manualOrderLines[`type${count}`] = "Kjøp";
      manualOrderLines[`mt${count}`] =
        order.type === "Limit" ? "Limit" : "Market";
      manualOrderLines[`limit${count}`] =
        order.type === "Limit" ? dd(order.limitPrice) : 0.0;
      manualOrderLines[`marketUnitPrice${count}`] = dd(
        order.marketUnitPrice ?? secDet?.unitPrice
      );
      manualOrderLines[`marketUnitPriceDate${count}`] = cleanVal(
        order.marketUnitPriceDate ?? secDet?.unitPriceDate
      );
      manualOrderLines[`secCcy${count}`] = cleanVal(secDet?.ccy);
      addManualSecurityMetadata(count, secDet, secCode);

      if (order.type === "Limit") {
        manualOrderLines[`omarketDur${count}`] = order.duration;
      }
      if (dd(order.amountInput) > 0) {
        manualOrderLines[`totAmount${count}`] = dd(order.amountInput);
        manualOrderLines[`totAmountFormatted${count}`] = `NOK ${df(
          order.amountInput,
          2
        )}`;
      } else if (dd(order.quantity) > 0) {
        manualOrderLines[`amount${count}`] = dd(order.quantity);
        manualOrderLines[`amountFormatted${count}`] = df(dd(order.quantity), 6);
      }
      count++;
    }
    for (const order of sellOrders) {
      if (!order.securityId) continue;
      const secDet = faSec.find((security) => security.id === order.securityId);
      manualOrderLines[`sec${count}`] = `${order.name}\n${order.securityCode}`;
      manualOrderLines[`secName${count}`] = `${order.name}`;
      manualOrderLines[`secIsin${count}`] = `${order.securityCode}`;
      manualOrderLines[`type${count}`] = "Salg";
      manualOrderLines[`mt${count}`] =
        order.type === "Limit" ? "Limit" : "Market";
      manualOrderLines[`limit${count}`] =
        order.type === "Limit" ? dd(order.limitPrice) : 0.0;
      manualOrderLines[`unitPrice${count}`] = dd(order.marketUnitPrice);
      manualOrderLines[`marketUnitPrice${count}`] = dd(order.marketUnitPrice);
      manualOrderLines[`marketUnitPriceDate${count}`] = cleanVal(
        order.marketUnitPriceDate ?? secDet?.unitPriceDate
      );
      manualOrderLines[`secCcy${count}`] = cleanVal(secDet?.ccy);
      addManualSecurityMetadata(count, secDet, order.securityCode);
      if (order.type === "Limit" && /^\d+$/.test(order.duration)) {
        manualOrderLines[`omarketDur${count}`] = order.duration;
      }
      manualOrderLines[`amount${count}`] = dd(order.sellQty);
      manualOrderLines[`amountFormatted${count}`] = df(order.sellQty, 6);
      count++;
    }
    if (isModel) {
      if (
        modelType === "CAPGUARDEKB" ||
        modelType === "CAPGUARDEIENDOM" ||
        modelType === "FONDSOBLIGASJON" ||
        modelPositions.length <= 1
      ) {
        const desc = getModelDescription(modelType);
        manualOrderLines[`sec${count}`] = `${desc}\n${modelType}`;
        manualOrderLines[`type${count}`] = "Kjøp";
        manualOrderLines[`mt${count}`] = "Rebalansering";
        manualOrderLines[`totAmount${count}`] = "NOK " + dd(portAmount);
        manualOrderLines[`totAmountFormatted${count}`] = `NOK ${df(
          portAmount,
          2
        )}`;
        addManualSecurityMetadata(count, undefined, modelType);
        count++;
      } else {
        for (const pos of modelPositions) {
          const posAmount = portAmount * (pos.share_of_portfolio / 100);
          const posName = pos.name ?? pos.description ?? pos.short_name;
          const posCode = pos.isin_code ?? pos.security_code ?? pos.short_name;
          manualOrderLines[`sec${count}`] = `${posName}\n${posCode}`;
          manualOrderLines[`type${count}`] = "Kjøp";
          manualOrderLines[`mt${count}`] = "Rebalansering";
          manualOrderLines[`totAmount${count}`] = "NOK " + dd(posAmount);
          manualOrderLines[`totAmountFormatted${count}`] = `NOK ${df(
            posAmount,
            2
          )}`;
          addManualSecurityMetadata(
            count,
            findPositionSecurity(pos),
            posCode,
            pos
          );
          count++;
        }
      }
    }
  }
  variables.manualOrderLines = manualOrderLines;
  variables.dataWarnings = dataWarnings;

  // ── 10. Dynamic order options (checkboxes in the manual order module) ──────
  variables.portDynamicSell = newData.portDynamicSell === true;
  variables.portDynamicWd = newData.portDynamicWd === true;

  // ── 11. Recurring savings agreement (nested — numbered keys) ───────────────
  const dpSavingsEnabled = newData.dpSavingsEnabled === true;
  const dpSavingsAmount = dd(newData.dpSavingsAmount);
  const dpSavingsDayOfMonth = cleanVal(newData.dpSavingsDayOfMonth);
  const dpSavingsAvtalegiro = newData.dpSavingsAvtalegiro === true;
  const dpSavingsDayNum = dd(newData.dpSavingsDayOfMonth);

  variables.dpSavingsEnabled = dpSavingsEnabled;
  variables.savAmount = "NOK " + dfzero(dpSavingsAmount);
  variables.savDate = dpSavingsDayOfMonth;
  variables.savAG = dpSavingsAvtalegiro ? "Ja" : "Nei";
  variables.savOrderDate = isModel
    ? "Handles i henhold til mandat"
    : dpSavingsDayNum < 9 || dpSavingsDayNum > 24
    ? "10"
    : "25";

  const dpSavingsFunds =
    (newData.dpSavingsFunds as Array<{ name: string; pct: number }>) || [];
  const savingsFunds: Record<string, string> = {};
  for (let n = 1; n <= 5; n++) {
    const fund = dpSavingsFunds[n - 1];
    if (fund && !isModel) {
      const pct = dd(fund.pct);
      const fundKr = Math.round((pct / 100) * dpSavingsAmount);
      savingsFunds[`fund${n}Name`] = cleanVal(fund.name);
      savingsFunds[`amount${n}`] =
        "NOK " + dfzero(fundKr) + " (" + dfzero(pct) + " %)";
    } else {
      savingsFunds[`fund${n}Name`] = "";
      savingsFunds[`amount${n}`] = "";
    }
  }
  variables.savingsFunds = savingsFunds;

  // ── 12. Recurring withdrawal agreement ─────────────────────────────────────
  const dpWithdrawalEnabled = newData.dpWithdrawalEnabled === true;
  const dpWdAmount = dd(newData.dpWdAmount);
  const dpWdDayOfMonth = cleanVal(newData.dpWdDayOfMonth);
  const dpWdAccount = fmtAccount(cleanVal(newData.dpWdAccount || ""));

  variables.dpWithdrawalEnabled = dpWithdrawalEnabled;
  variables.portWdAccount = dpWdAccount;
  variables.portFixedWdDate = dpWdDayOfMonth;
  variables.portFixedWdAmount = "NOK " + dfzero(dpWdAmount);

  return { ...baseParams, ...variables };
}

// ---------------------------------------------------------------------------
// Generic (non-order) variable collection — spreads every primitive field,
// no gating on which fields "count".
// ---------------------------------------------------------------------------

function collectGenericVariables(
  doc: DocProducerDocument,
  baseParams: Record<string, string>
): Record<string, unknown> {
  const raw = doc.data as Record<string, unknown>;
  const data = (raw?.newData ?? raw) as Record<string, unknown>;
  const variables: Record<string, unknown> = { ...baseParams };
  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined) continue;
    variables[key] = value;
  }
  return variables;
}

function isProfileDocument(doc: DocProducerDocument): boolean {
  return (
    doc.docType === "kundeprofil" ||
    doc.docType === "mifidForm" ||
    doc.docType === "amlForm" ||
    doc.docType === "documentFromSecurity"
  );
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

/**
 * Translates the queued DocProducer documents into one entry per document —
 * unlike `buildDocumentProductionList`, this never explodes a single "order"
 * document into several production-ready sub-documents (account agreement,
 * one-off deposit, transfer, savings, ...). Every variable that could ever be
 * relevant for that document is always collected, regardless of whether the
 * underlying amount/toggle is actually set — deciding which documents to
 * actually produce is the backend's responsibility now.
 *
 * Intentionally does not call `buildDocumentProductionList` — it only reuses
 * the shared, pure formatting/fee helpers from that module.
 */
export function buildDocumentListWithVariables(
  contact: Contact,
  avdData: Contact | null | undefined,
  documents: DocProducerDocument[],
  faModels: QueryModelPosition[],
  faSec: QuerySecurity[],
  profileData: QueryProfile[],
  // Kept for signature parity with buildDocumentProductionList; not used —
  // the backend now owns the logic that depends on these.
  _activePorts: Portfolio[],
  _pendingOrders: PendingOrder[],
  docProdOut: Record<string, unknown> | null = null
): DocumentListWithVariables {
  const baseParams = getStdParams(contact, avdData);
  const kundeprofil = profileData.filter((x) =>
    x.attributeKey.includes("cgProfiling")
  );
  const orders: InternalTransferOrder[] = documents
    .filter((document) => document.docType === "order")
    .map((document) => {
      const raw = document.data as Record<string, unknown>;
      const newData = (raw.newData ?? raw) as Record<string, unknown>;
      const existData = (raw.existData ?? {}) as Record<string, unknown>;
      return {
        id: document.id,
        portfolioId: cleanVal(newData.dpPortfolioId ?? existData.dpPortfolioId),
        portType: cleanVal(newData.dpPortType ?? existData.dpPortType),
      };
    });
  return {
    standardParams: baseParams,
    kundeprofil: kundeprofil,
    documents: documents.map((doc) => {
      const variables =
        doc.docType === "order"
          ? collectOrderVariables(doc, baseParams, faModels, faSec, orders)
          : collectGenericVariables(doc, baseParams);

      if (isProfileDocument(doc)) {
        if (docProdOut) variables.docProdOut = docProdOut;
      }

      return {
        id: doc.id,
        type: doc.docType,
        data: variables,
      };
    }),
  };
}
